import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { YearlyCalculation } from '@/types/businesscase';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useChartExport } from '@/hooks/useChartExport';

interface CashflowChartProps {
  data: YearlyCalculation[];
  businesscaseName?: string;
}

const chartConfig = {
  cashflowBase: {
    label: 'Base Scenario',
    color: 'hsl(var(--primary))',
  },
  cashflowBest: {
    label: 'Best Case (+15%)',
    color: 'hsl(142, 76%, 36%)', // green
  },
  cashflowWorst: {
    label: 'Worst Case (-10%)',
    color: 'hsl(24, 95%, 53%)', // orange
  },
};

export function CashflowChart({ data, businesscaseName }: CashflowChartProps) {
  const { exportToPNG } = useChartExport();
  
  const formatCurrency = (value: number) => {
    return `€${(value / 1000).toFixed(0)}k`;
  };

  return (
    <Card className="border-border" id="cashflow-chart">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Cashflow Analyse per Jaar</CardTitle>
            <CardDescription>
              Verwachte jaarlijkse cashflow in drie scenario's
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportToPNG('chart-cashflow', 'cashflow', businesscaseName)}
          >
            <Download className="w-4 h-4 mr-2" />
            PNG
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="year" 
                stroke="hsl(var(--muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                tickFormatter={formatCurrency}
                style={{ fontSize: '12px' }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              
              <Line 
                type="monotone" 
                dataKey="cashflowBase" 
                stroke={chartConfig.cashflowBase.color}
                strokeWidth={3}
                name={chartConfig.cashflowBase.label}
                dot={{ r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="cashflowBest" 
                stroke={chartConfig.cashflowBest.color}
                strokeWidth={2}
                strokeDasharray="5 5"
                name={chartConfig.cashflowBest.label}
                dot={{ r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="cashflowWorst" 
                stroke={chartConfig.cashflowWorst.color}
                strokeWidth={2}
                strokeDasharray="5 5"
                name={chartConfig.cashflowWorst.label}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
